import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  model,
  OnInit,
  output,
  signal,
  untracked,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { IBankDetails, IIBANFormConfig } from '@mwe/models';
import {
  anyTrue,
  focusFirstInvalidInput,
  isProductCategoryEmobility,
  isProductCategoryFernwaerme,
  isProductCategoryInternet,
  isProductCategoryStromOrGas,
  removeAllSpaces,
} from '@mwe/utils';
import { EnvironmentService, InvoiceDataService, LoggingService } from '@mwe/services';
import { InfoboxComponent } from '../infobox/infobox.component';
import { NgClass } from '@angular/common';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { ExpanderComponent } from '../expander/expander.component';
import { noop } from 'rxjs';

@Component({
  selector: 'mwe-iban-form',
  templateUrl: './iban-form.component.html',
  imports: [InfoboxComponent, FormsModule, ReactiveFormsModule, NgClass, TranslateDirective, ExpanderComponent, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IBANFormComponent implements OnInit {
  private readonly formBuilder = inject(UntypedFormBuilder);
  private readonly bankAccountService = inject(InvoiceDataService);
  private readonly loggingService = inject(LoggingService);
  private readonly environmentService = inject(EnvironmentService);
  private readonly injector = inject(Injector);

  readonly config = model<IIBANFormConfig>();
  readonly productCategories = input<string[]>([]);
  readonly hideIbanLegalInfo = input<boolean>(false); // can be removed once VKI is no longer needed
  readonly formValidating = output<boolean>();
  readonly formSubmitted = output<IBankDetails>();
  readonly validationErrors = output<boolean>();

  form: UntypedFormGroup;
  ibanControl: UntypedFormControl;
  httpPromise: Promise<IBankDetails>;
  errorDescription: Map<string, string> = new Map();
  ibanControlName = 'mwe-iban-form-iban';

  readonly iban = signal<string>('');
  readonly isValid = signal<boolean>(false);
  readonly inputAriaValid = signal<boolean | undefined>(undefined);
  readonly isValidating = signal<boolean>(false);

  readonly error = signal<boolean>(false);
  readonly errorCode = signal<string>(undefined);

  get showError(): boolean {
    return (
      !this.isValidating() && ((this.error() && this.errorCode() !== '0000') || (this.ibanControl.errors && !this.ibanControl.pristine))
    );
  }

  readonly ibanErrorKey = computed(() => {
    const code = this.errorCode();
    // if the config has an ibanErrorKey, use it, otherwise compute the key (but register errorCode as dependency first)
    if (this.config().ibanErrorKey) return this.config().ibanErrorKey;

    return code ? this.errorDescription.get(code) : 'ibanForm.iban.error';
  });

  ibanLegalInfoTextKey = computed(() =>
    isProductCategoryInternet(this.productCategories()?.[0] ?? '')
      ? 'invoiceInformation.invoiceHow.infoInternet'
      : 'invoiceInformation.invoiceHow.info',
  );

  readonly link = computed<string>(() => {
    const isSG = isProductCategoryStromOrGas(this.productCategories()?.[0] ?? '');
    const isWaerme = isProductCategoryFernwaerme(this.productCategories()?.[0] ?? '');
    const isEmobility = isProductCategoryEmobility(this.productCategories()?.[0] ?? '');
    const isInternet = isProductCategoryInternet(this.productCategories()?.[0] ?? '');
    const isB2B = this.environmentService.getPortalCustomerType() === 'business';

    if (isB2B) {
      if (isSG) {
        return 'https://www.wienenergie.at/business/hilfe-und-kontakt/kontakt/kontaktthemen-business/?thema=Strom%20%26%20Erdgas_Kund*innendaten_Bezahlart%20%C3%A4ndern&formular=1';
      }

      if (isWaerme) {
        return 'https://www.wienenergie.at/business/hilfe-und-kontakt/kontakt/kontaktthemen-business/?thema=W%C3%A4rme_Kund*innendaten_Bezahlart%20%C3%A4ndern&formular=1';
      }

      if (isEmobility) {
        return 'https://www.wienenergie.at/business/hilfe-und-kontakt/kontakt/kontaktthemen-business/?thema=E-Mobilit%C3%A4t_Kund*innendaten_Bezahlart%20%C3%A4ndern&formular=1';
      }

      return 'https://www.wienenergie.at/business/hilfe-und-kontakt/';
    }

    if (isSG) {
      return 'https://www.wienenergie.at/privat/hilfe-und-kontakt/kontakt-servicezentren/kontaktthemen/?thema=Strom%20%26%20Erdgas_Kund*innendaten_Bezahlart%20%C3%A4ndern&formular=1';
    }

    if (isWaerme) {
      return 'https://www.wienenergie.at/privat/hilfe-und-kontakt/kontakt-servicezentren/kontaktthemen/?thema=W%C3%A4rme_Kund*innendaten_Bezahlart%20%C3%A4ndern&formular=1';
    }

    if (isEmobility) {
      return 'https://www.wienenergie.at/privat/hilfe-und-kontakt/kontakt-servicezentren/kontaktthemen/?thema=E-Mobilit%C3%A4t_Kund*innendaten_Bezahlart%20%C3%A4ndern&formular=1';
    }

    if (isInternet) {
      return 'https://www.wienenergie.at/privat/hilfe-und-kontakt/kontakt-servicezentren/kontaktthemen/?thema=Internet%20%26%20TV_Kund*innendaten_Bezahlart%20%C3%A4ndern&formular=1';
    }

    return 'https://www.wienenergie.at/privat/hilfe-und-kontakt/';
  });

  constructor() {
    effect(() => {
      // Ensure default labels (ibanPlaceholderKey, ibanLabelKey) are always present.
      const ibanConfig = this.config();

      untracked(() => {
        if (!ibanConfig?.ibanPlaceholderKey || !ibanConfig?.ibanLabelKey) this.setDefaultLabels();
      });

      this.initFormModel();
    });
  }

  ngOnInit(): void {
    let errorCodes = ['0000', '1004', '1010', '1011', '1012', '1013'];
    errorCodes.forEach(code => this.errorDescription.set(code, `ibanForm.errors.${code}.message`));
    errorCodes = [];

    for (let i = 1; i < 20; i++) errorCodes.push('' + (7000 + i));

    errorCodes.forEach(code =>
      this.errorDescription.set(code, `ibanForm.errors.7001_7019.message_${this.environmentService.getPortalCustomerType()}`),
    );
    this.setDefaultLabels();
  }

  async validateForm(): Promise<void> {
    if (!this.isValidating()) {
      Object.keys(this.form.controls).forEach(controlName => this.form.controls[controlName].markAsDirty());
      return anyTrue(this.error(), !!this.ibanControl.errors) ? Promise.reject() : Promise.resolve();
    }

    await Promise.all([this.httpPromise])
      .then(() => {
        // sometime on click weiter we need some timeout
        // else it's not moving to next page
        setTimeout(noop, 10);
        return Promise.resolve();
      })
      .catch(() => Promise.reject());
  }

  async validate(): Promise<void> {
    const value = this.iban();
    if (anyTrue(this.isValid(), this.isValidating())) return;

    if (value?.length > 12) {
      this.emitValidating(true);

      const ibanStr = removeAllSpaces(this.iban());
      try {
        this.httpPromise = this.bankAccountService.checkBankDetails(ibanStr, this.config().processId);
        const payload = await this.httpPromise;
        this.inputAriaValid.set(true);

        this.resetErrors();

        if (payload) this.validateBankDetails(payload);
      } catch (ex) {
        this.inputAriaValid.set(false);
        this.resetErrors();
        await this.loggingService.logError(ex, `validation failed`);

        if (ex.system === 'IL') {
          if (ex.error?.code) {
            if (this.errorDescription.has(ex.error.code)) {
              this.error.set(true);
              this.errorCode.set(ex.error.code);
              this.emitErrorValues();
              const codeValue = Number(ex.error.code);

              if (codeValue > 7000 && codeValue < 7020) {
                this.error.set(true);
              }
            } else {
              this.emitErrorValues();
            }
          } else {
            this.emitErrorValues();
          }
        } else {
          this.error.set(true);
          this.errorCode.set('0000');
          this.emitValidating(false);
        }
      }
    } else if (value?.length > 1) {
      this.emitErrorValues();
    }
  }

  onInputIBAN(newValue: string): void {
    this.isValid.set(false);
    this.iban.set(newValue);
  }

  validateBankDetails(bankDetails: IBankDetails): void {
    const bankdaten = bankDetails.bankdaten;

    if (bankdaten) {
      bankDetails.iban = removeAllSpaces(this.iban());
      if (bankdaten.bic === '-') bankdaten.bic = '';
      this.isValid.set(true);
      this.formSubmitted.emit(bankDetails);
      this.emitValidating(false);
    } else {
      this.emitErrorValues();
    }
  }

  private initFormModel(): void {
    this.iban.set(this.config().iban);

    untracked(() => {
      const group = {};
      this.ibanControl = new UntypedFormControl(this.iban() || '', {
        validators: [Validators.required, Validators.minLength(2)],
        updateOn: 'blur',
      });
      group[this.ibanControlName] = this.ibanControl;
      this.form = this.formBuilder.group(group);
    });
  }

  private setDefaultLabels(): void {
    if (!this.config()) {
      this.config.set({});
    }

    if (!this.config().iban) {
      this.config.update(config => ({ ...config, iban: '' }));
    }

    if (!this.config().bic) {
      this.config.update(config => ({ ...config, bic: '' }));
    }

    if (!this.config().ibanLabelKey) {
      this.config.update(config => ({ ...config, ibanLabelKey: 'ibanForm.iban.label' }));
    }

    if (!this.config().ibanPlaceholderKey) {
      this.config.update(config => ({ ...config, ibanPlaceholderKey: 'ibanForm.iban.placeholder' }));
    }
  }

  private emitErrorValues(): void {
    this.error.set(true);
    this.validationErrors.emit(true);
    this.emitValidating(false);
    focusFirstInvalidInput(this.injector);
  }

  private emitValidating(validating: boolean): void {
    this.isValidating.set(validating);
    this.formValidating.emit(this.isValidating());
  }

  private resetErrors(): void {
    this.error.set(false);
    this.errorCode.set(undefined);
    this.isValid.set(false);
  }
}
